import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { db, FirebaseDocRefType } from 'firebase/index'
import { Action, Dispatch } from 'redux'
import { State } from 'index'
import { eachDayOfInterval, format, isSameDay } from 'date-fns'
import { dateToString, isHolidayOfStore, parseToDate } from 'lib/helper'
import { setClosedDatesAction } from 'reducks/calendar/actions'
import { StoreCalendarType } from 'types/storeCalendar'
import { fetchRetailerStores } from 'reducks/store/operations'
import { MELLOW_SHOP_REF } from 'constants/shop'
import _ from 'lodash'
const daysOfWeek = new DaysOfWeek()

const fetchStoreCalendars = (storeRef: FirebaseDocRefType, start: Date): Promise<StoreCalendarType[]> => {
  const calendarsRef = storeRef.collection('store_calendars')
  return calendarsRef
    .orderBy('date', 'asc')
    .startAt(format(start, 'yyyyMMdd'))
    .get()
    .then((snapshots) => {
      const calendars: StoreCalendarType[] = []
      snapshots.forEach((snapshot) => {
        const data = snapshot.data() as StoreCalendarType
        calendars.push(data)
      })
      return calendars
    })
}

const fetchRetailerClosedDates = (start: Date, end: Date, holidays: DayOfWeekType[], storeRef: FirebaseDocRefType) => {
  return async (dispatch: Dispatch<Action>, getState: () => State) => {
    const specialHolidays = getState().calendar.specialHolidays
    // 生産店舗の定休日の取得
    const datesOfMonth: Date[] = eachDayOfInterval({ start: start, end: end })
    const manufacturerClosedDates: Date[] = datesOfMonth.filter((date) =>
      isHolidayOfStore(date, holidays, specialHolidays)
    )
    // 販売店舗の一覧を取得
    const retailerStores = await fetchRetailerStores(MELLOW_SHOP_REF)
    // 販売店舗の共通の定休日を取得
    const retailerHolidays = daysOfWeek
      .getDaysOfWeek()
      .filter((daysOfWeek) => retailerStores.every((store) => store.holidays.includes(daysOfWeek)))
    const retailerClosedDates: Date[] = datesOfMonth.filter((date) =>
      isHolidayOfStore(date, retailerHolidays, specialHolidays)
    )
    let closedDates: Date[] = [...manufacturerClosedDates, ...retailerClosedDates]
    // 生産店舗と販売店舗のstoreCalendarsを取得
    const manufacturerStoreCalendars = await fetchStoreCalendars(storeRef, start)
    const promises: Promise<StoreCalendarType[]>[] = []
    retailerStores.map((store) => {
      const ref = db.collection('stores').doc(store.store_id)
      promises.push(fetchStoreCalendars(ref, start))
    })
    const result: StoreCalendarType[][] = await Promise.all(promises)
    const retailerStoreCalendars: StoreCalendarType[] = result.flat()
    // 生産店舗の臨時休業日・臨時営業日を反映する
    for (let i = 0; i < manufacturerStoreCalendars.length; i = (i + 1) | 0) {
      const calendar = manufacturerStoreCalendars[i]
      // 生産店舗の臨時休業日を追加
      if (calendar.status === 'close') {
        closedDates.push(parseToDate(calendar.date))
      }
      // 生産店舗の臨時営業日かつ販売店舗の休業日でなけれ削除
      if (
        calendar.status === 'open' &&
        !retailerClosedDates.some((closedDate) => isSameDay(closedDate, parseToDate(calendar.date)))
      ) {
        closedDates = closedDates.filter((closedDate) => !isSameDay(closedDate, parseToDate(calendar.date)))
      }
    }
    // 販売店舗の臨時休業日・臨時営業日を反映する
    for (let i = 0; i < retailerStoreCalendars.length; i = (i + 1) | 0) {
      const calendar = retailerStoreCalendars[i]
      // 販売店舗の臨時休業日を追加
      if (calendar.status === 'close') {
        closedDates.push(parseToDate(calendar.date))
      }
      // 販売店舗の臨時営業日かつ生産店舗の休業日でなけれ削除
      if (
        calendar.status === 'open' &&
        !manufacturerClosedDates.some((closedDate) => isSameDay(closedDate, parseToDate(calendar.date)))
      ) {
        closedDates = closedDates.filter((closedDate) => !isSameDay(closedDate, parseToDate(calendar.date)))
      }
    }
    // 重複している日付を排除
    const deduplicatedClosedDates = _.uniqBy(closedDates, (date) => dateToString(date))
    dispatch(setClosedDatesAction(deduplicatedClosedDates))
  }
}

export default fetchRetailerClosedDates
